import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { Badge } from '@lib/components'
import type { TreeFrame as TreeFrameType } from '@octopus-do/octopus-project-core'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { openModal } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useProjectAccess } from '@/hooks'
import { PROJECT_SETTINGS_PERMISSION } from '@/permissions/project/definitions'
import { Selector } from '../../../Selector'
import css from '../../SettingsItem.module.scss'
import MobileFrame from './icons/MobileFrame'
import NeutralFrame from './icons/NeutralFrame'
import WebFrame from './icons/WebFrame'

type Props = {
  inline?: boolean
  disabled?: boolean
}

const TreeFrame: React.FC<Props> = ({ inline = false, disabled = false }) => {
  const dispatch = useDispatch()

  const { changeProjectFrameType } = useProjectLegacyActions()

  const { hasNotAvailableAccessBadge, badgeType } = useProjectAccess(
    PROJECT_SETTINGS_PERMISSION.FRAMES
  )

  const frame = useSelector(migrationProjectSelector.getProjectFrame)

  const onClick = (frame: TreeFrameType) => {
    if (hasNotAvailableAccessBadge) {
      return
    }

    sendGtmEvent(
      GtmTriggerName.PROJECT_FRAME,
      'CLick to change frame from sidebar'
    )

    changeProjectFrameType({ frame })
  }

  const handleBadgeClick = () => {
    sendGtmEvent(GtmTriggerName.UPGRADE_FRAMES)
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  const selectorClasses = {
    container: css.selector,
  }

  const selectorDisabled = hasNotAvailableAccessBadge || disabled

  return (
    <div
      className={cx(css.container, {
        [css.disabled]: hasNotAvailableAccessBadge,
        [css.inline]: inline,
      })}
    >
      <div className={css.titleWrapper}>
        <div className={css.title}>Frames</div>

        {hasNotAvailableAccessBadge && (
          <div onClick={handleBadgeClick}>
            <Badge type={badgeType} />
          </div>
        )}
      </div>

      <div className={css.selectors}>
        <Selector
          active={frame === 'web'}
          disabled={selectorDisabled}
          title="Web"
          classnames={selectorClasses}
          icon={<WebFrame disabled={selectorDisabled} />}
          onClick={() => onClick('web')}
        />

        <Selector
          active={frame === 'mobile'}
          disabled={selectorDisabled}
          title="Mobile"
          classnames={selectorClasses}
          icon={<MobileFrame disabled={selectorDisabled} />}
          onClick={() => onClick('mobile')}
        />

        <Selector
          active={frame === 'neutral'}
          disabled={selectorDisabled}
          title="Neutral"
          classnames={selectorClasses}
          icon={<NeutralFrame disabled={selectorDisabled} />}
          onClick={() => onClick('neutral')}
        />
      </div>
    </div>
  )
}

export default TreeFrame
